// @todo: Migrate to 'EditableField' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import EditableField from '@wandb/weave/common/components/EditableField';
import {SingleLineText} from '@wandb/weave/common/components/elements/Text';
import {hexToRGB, SIENNA_300} from '@wandb/weave/common/css/globals.styles';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {clsx} from 'clsx';
import {pick} from 'lodash';
import React, {useEffect} from 'react';
// @todo: Migrate to 'Link' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Link} from 'react-router-dom';

import {useViewViewMutation} from '../../../generated/graphql';
import * as ReportTypes from '../../../state/reports/types';
import * as ViewActions from '../../../state/views/actions';
import {usePartMaybe, useViewRefAction} from '../../../state/views/hooks';
import {type ReportPageNormalized} from '../../../state/views/report/types';
import {isOwnView} from '../../../state/views/util';
import {viewingAs} from '../../../util/admin';
import {useDatadogLogs} from '../../../util/datadog/datadogLogs';
import {profilePage as profilePageUrl} from '../../../util/urls';
import {useLoadedReportContext} from '../LoadedReportContext';
import {useReportPageContext} from '../ReportPageContext';
import {ReportPageViewHeader} from './ReportPageViewHeader';

type ReportPageViewContentHeaderProps = {
  fullWidth?: boolean;
};

export const ReportPageViewContentHeader = ({
  fullWidth,
}: ReportPageViewContentHeaderProps) => {
  const {viewer, isWBFeaturedReport, rootReportId, isAdmin} =
    useReportPageContext();
  const {reportViewRef, view} = useLoadedReportContext();
  const datadogLogs = useDatadogLogs();

  const [viewView] = useViewViewMutation();
  useEffect(() => {
    // viewView will fail with permission error if in impersonating mode
    if (viewingAs() !== '') {
      return;
    }

    try {
      // This mutation increments the view count for this report
      viewView({variables: {id: rootReportId}});
    } catch (error) {
      console.error(error);
    }
  }, [rootReportId, viewView]);

  const setNotes = useViewRefAction(reportViewRef, ViewActions.setDescription);
  const notes = view.description || '';

  const viewPart = usePartMaybe(view.partRef) as ReportPageNormalized | null;
  const viewAuthors = viewPart?.authors ?? [];
  let authors;
  if (viewAuthors.length > 0) {
    authors = viewAuthors;
  } else {
    authors = [pick(view.user, 'username', 'name')];
  }
  const viewerOwnsReport =
    isOwnView(viewer, view) ||
    viewAuthors.some(author => author?.id === viewer?.id);

  useEffect(() => {
    if (view.type === ReportTypes.REPORT_DRAFT_VIEW_TYPE) {
      datadogLogs.logger.info('Report preview mode reached');
    }
  }, [view.type, datadogLogs]);

  return (
    <div className="report-header-view" data-test="report-page-view-header">
      {view.type === ReportTypes.REPORT_DRAFT_VIEW_TYPE && (
        // Since reports are now WYSIWYG, preview mode is obsolete,
        // so this state should not be possible to get to via the UI.
        // I'm leaving a little message here in case someone somehow gets to
        // this type of URL.
        <div
          style={{
            background: hexToRGB(SIENNA_300, 0.48),
            padding: '16px',
            fontFamily: 'Source sans pro',
          }}>
          You are currently previewing a{' '}
          {view.parentId ? 'draft edit of a report' : 'draft of a new report'}.
          This draft is private to{' '}
          {viewerOwnsReport ? 'you' : view.user.username}. Report previews are a
          deprecated feature; if you arrived here accidentally, let us know how.
        </div>
      )}
      <div
        className={clsx('report-header-view__content', {
          'full-width': fullWidth,
        })}>
        <div className="report-header-view__title">
          <div className="report-header-view__title-name">
            <SingleLineText as="h1">{view.displayName}</SingleLineText>
            {notes !== '' && (
              <div className="report-header-view__description">
                <EditableField
                  multiline
                  maxLength={512}
                  readOnly={true}
                  placeholder={'Add a description...'}
                  value={notes}
                  // Report metadata is loaded asynchronously so we need to support
                  // dynamic updates to the description.
                  updateValue={true}
                  save={setNotes}
                />
              </div>
            )}
            <div className="report-header-view__meta">
              <div className="report-header-view__authors">
                {authors.map(({username, name}, i) => (
                  <React.Fragment key={username}>
                    <Link
                      className="report-header-view__author"
                      to={profilePageUrl(
                        // incase the username is empty or the entity is empty, this will default to the home page
                        username.length > 0 || !view.project?.entityName
                          ? username
                          : view.project?.entityName
                      )}>
                      {name || username}
                    </Link>
                    {i < authors.length - 1 && ', '}
                  </React.Fragment>
                ))}
              </div>
              <div className="report-header-view__actions">
                {isWBFeaturedReport &&
                  !isAdmin &&
                  view.type === ReportTypes.REPORT_VIEW_TYPE && (
                    <ReportPageViewHeader viewerOwnsReport={viewerOwnsReport} />
                  )}
              </div>
            </div>
            <TailwindContents>
              <div className="flex text-sm text-moon-450">
                <span className="">
                  Created on{' '}
                  {new Date(view.createdAt).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
                <span className="pl-5 pr-5">|</span>
                <span className="">
                  Updated on{' '}
                  {new Date(view.updatedAt).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </TailwindContents>
          </div>
        </div>
      </div>
    </div>
  );
};
